import React from 'react';
import PropTypes from 'prop-types';
import Image from 'components/image';
import LinkButton, { themes as linkButtonThemes } from 'components/link-button';
import FloatingTags from 'components/floating-tags';
import FloatingTag from 'components/floating-tag';
import Video from 'components/video';
import ScrollButton from 'components/scroll-button';
import cn from 'classnames';
import { getFluidVideoPreviewImageSources } from 'js/video-source-utils';

const buildMobileSources = imageSrc => {
  return [
    {
      imageSrc,
      width: 350,
      height: 283,
      media: 390,
    },
    {
      imageSrc,
      width: 390,
      height: 315,
      media: 430,
    },
    {
      imageSrc,
      width: 728,
      height: 333,
      media: 768,
    },
  ];
};

const buildDesktopSources = imageSrc => {
  return [
    // Tablet devices
    {
      imageSrc,
      width: 984,
      height: 256,
      media: 1024,
    },
    // Desktop devices
    {
      imageSrc,
      width: 620,
      height: 600,
      media: 1280,
    },
    {
      imageSrc,
      width: 663,
      height: 568,
      media: 1366,
    },
    {
      imageSrc,
      width: 820,
      height: 790,
      media: 1920,
    },
    {
      imageSrc,
      width: 820,
      height: 1150,
      media: 2560,
    },
  ];
};

const getImageSources = ({ desktopImageSrc, mobileImageSrc }) => {
  return [
    ...buildMobileSources(mobileImageSrc),
    ...buildDesktopSources(desktopImageSrc),
  ];
};

const TopBannerBlock = ({
  image,
  video,
  tags,
  textLine1,
  textLine2,
  seeAllButtonLink,
  backgroundColor,
  lang = {
    scrollButtonAriaLabel: 'Snarvei til hovedinnholdet på siden',
  },
}) => {
  const ContentElement = seeAllButtonLink && seeAllButtonLink.url ? 'a' : 'div';

  const contentElementProps = {
    ...(seeAllButtonLink && {
      href: seeAllButtonLink.url,
      target: seeAllButtonLink.openInNewTab ? '_blank' : null,
    }),
  };

  return (
    <div className="top-banner-block">
      <div className="top-banner-block__container">
        <ContentElement
          {...contentElementProps}
          className={cn('top-banner-block__content', {
            'top-banner-block__link': seeAllButtonLink,
            'top-banner-block__link_without-video': seeAllButtonLink && !video,
          })}
          style={{ backgroundColor }}
        >
          {(textLine1 || textLine2) && (
            <FloatingTags
              items={tags}
              className="top-banner-block__heading-tags"
            >
              <h1 className="top-banner-block__heading">
                {textLine1 && (
                  <span className="top-banner-block__heading-line">
                    {textLine1}
                  </span>
                )}
                {textLine2 && (
                  <span className="top-banner-block__heading-line">
                    {textLine2}
                  </span>
                )}
              </h1>
            </FloatingTags>
          )}

          {seeAllButtonLink && (
            <div className="top-banner-block__btn-holder">
              <LinkButton
                text={seeAllButtonLink.text}
                theme={linkButtonThemes.lightHover}
                className="top-banner-block__btn"
              />
            </div>
          )}
        </ContentElement>
        <div className="top-banner-block__media">
          {video ? (
            <Video
              {...video}
              fluid={true}
              imageSettings={{
                fluid: true,
                className: 'image_size_top-banner-image',
                sources: getFluidVideoPreviewImageSources({
                  portraitSource: video?.portraitSource,
                  landscapeSource: video?.landscapeSource,
                  buildVideoPreviewMobileSources: buildMobileSources,
                  buildVideoPreviewDesktopSources: buildDesktopSources,
                }),
                fetchPriority: 'high',
              }}
            />
          ) : (
            image && (
              <Image
                className="top-banner-block__image image_size_top-banner-image"
                {...image}
                sources={getImageSources({
                  desktopImageSrc: image.src,
                  mobileImageSrc: image.src,
                })}
                fetchPriority="high"
                fluid={true}
              />
            )
          )}
        </div>
      </div>
      <div className="top-banner-block__bottom-panel">
        <ScrollButton
          className="top-banner-block__scroll-btn"
          iconName="arrow-top"
          targetElement=".content-area"
          ariaLabel={lang.scrollButtonAriaLabel}
        />
      </div>
    </div>
  );
};

TopBannerBlock.propTypes = {
  tags: PropTypes.arrayOf(PropTypes.exact(FloatingTag.propTypes)),
  image: PropTypes.exact(Image.propTypes),
  video: PropTypes.exact(Video.propTypes),
  textLine1: PropTypes.string,
  textLine2: PropTypes.string,
  seeAllButtonLink: PropTypes.exact(LinkButton.propTypes),
  backgroundColor: PropTypes.string,
  lang: PropTypes.shape({
    scrollButtonAriaLabel: PropTypes.string.isRequired,
  }).isRequired,
};

export default TopBannerBlock;
